'use client';

import { Element } from 'html-react-parser';
import { parseRichText } from '@/app/_lib/parse-rich-text';
import { HubspotForm } from './hubspot-form';

interface FormEmbedFormHandlerProps {
  html: string | undefined | null;
  variant?: '1col' | '2col';
}

interface HubspotData {
  portalId: string;
  formId: string;
  region: 'eu1' | 'na1';
}

/**
 * Custom form content handler for Sparks University.
 *
 * Extracts HubSpot embed data from CMS HTML and renders the HubspotForm SDK component.
 * Script tags are removed since the SDK loads itself.
 */
export function FormEmbedFormHandler({
  html,
  variant = '2col',
}: FormEmbedFormHandlerProps) {
  if (!html) return null;

  const content = parseRichText(html, {
    customReplace: (element) => {
      // Remove script tags (HubSpot SDK loads itself)
      if (element.name === 'script') {
        return <></>;
      }

      // Replace HubSpot embed div with SDK component
      if (element.name === 'div') {
        const hubspotData = extractHubspotDataFromElement(element);
        if (hubspotData) {
          return (
            <HubspotForm
              portalId={hubspotData.portalId}
              formId={hubspotData.formId}
              region={hubspotData.region}
            />
          );
        }
      }

      // Return undefined to use standard parseRichText handling
      return undefined;
    },
  });

  return (
    <div className={variant === '2col' ? 'lg:-ml-16' : 'w-full'}>{content}</div>
  );
}

// Extract HubSpot data from a DOM element's attributes
function extractHubspotDataFromElement(element: Element): HubspotData | null {
  const portalId = element.attribs['data-portal-id'];
  const formId = element.attribs['data-form-id'];
  const region = element.attribs['data-region'];

  if (!portalId || !formId) return null;

  const validRegion = region === 'eu1' || region === 'na1' ? region : 'eu1';

  return { portalId, formId, region: validRegion };
}
